import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fab, IconButton } from '@mui/material'
import PageLoading from '../../../components/PageLoading/PageLoading'
import TableHeader from '../../../components/TableHeader/TableHeader'
import InformationTable from '../../../components/Table/InformationTable'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import {
  createGuion,
  deleteGuion,
  getGuionList,
  updateGuion
} from '../../../utils/api/materialDocente'
import { textFilter } from '../../../utils/table'
import ModalEditarGuion from '../../../modals/ModalEditarGuion/ModalEditarGuion'
import ModalConfirmacion from '../../../modals/ModalConfirmacion/ModalConfirmacion'
import { isRolBrain } from '../../../utils/auth'
import ModalNuevoGuion from '../../../modals/ModalNuevoGuion/ModalNuevoGuion'
import ModalAsignaturas from '../../../modals/ModalAsignaturas/ModalAsignaturas'
import ModalPdfViewer from '../../../modals/ModalPdfViewer/ModalPdfViewer'
import AsignaturaSelector from '../../../components/form/AsignaturaSelector/AsignaturaSelector'
import { pluralize } from '../../../utils/string'
import css from './Guiones.module.css'

const Guiones = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()
  const [search, setSearch] = useState('')
  const [guionSeleccionado, setGuionSeleccionado] = useState(null)
  const [isOpenEditarGuion, setIsOpenEditarGuion] = useState(false)
  const [isOpenEliminarGuion, setIsOpenEliminarGuion] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOpenAsignaturasModal, setIsOpenAsignaturasModal] = useState(false)
  const [isOpenPdfModal, setIsOpenPdfModal] = useState(false)
  const [selectedAsignatura, setSelectedAsignatura] = useState(null)

  const accesoBrain = isRolBrain()

  const { isLoading, data: guionList = [] } = useQuery({
    queryKey: ['material', 'guion'],
    queryFn: () =>
      getGuionList().catch(err => {
        notification.error({
          title: 'Error al recuperar los guiones',
          content: err
        })
        return []
      })
  })

  const { isPending: isGuionCreating, mutate: crearGuion } = useMutation({
    mutationFn: createGuion,
    onSuccess: () => {
      notification.success({
        title: 'Guion enviado',
        content: 'Se ha enviado el guion'
      })
      queryClient.invalidateQueries({ queryKey: ['material', 'guion'] })
      setIsModalOpen(false)
    },
    onError: err => {
      notification.error({ title: 'Error al enviar el guion', content: err })
    }
  })

  const { isPending: isGuionUpdating, mutate: actualizarGuion } = useMutation({
    mutationFn: updateGuion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material', 'guion'] })
      notification.success({
        title: 'Guion actualizado',
        content: 'Datos modificados correctamente'
      })
      setIsOpenEditarGuion(false)
    },
    onError: err => {
      notification.error({ title: 'Error actualizando el guion', content: err })
    }
  })

  const { isPending: isGuionDeleting, mutate: borrarGuion } = useMutation({
    mutationFn: deleteGuion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material', 'guion'] })
      notification.success({
        title: 'Guion borrado',
        content: 'Se ha borrado el guion correctamente'
      })
      setGuionSeleccionado(null)
      setIsOpenEliminarGuion(false)
      closeModal()
    },
    onError: err => {
      notification.error({ title: 'Error al borrar el guion', content: err })
    }
  })

  const handleNuevoGuion = ({ nombre, asignaturas, archivo }) => {
    if (isGuionCreating) return
    crearGuion({ nombre, asignaturas, archivo })
  }

  const handleUpdateGuion = updatedGuion => {
    if (isGuionUpdating) return
    actualizarGuion(updatedGuion)
  }

  const handleDeleteGuion = () => {
    if (isGuionDeleting) return
    borrarGuion(guionSeleccionado.id)
  }

  const filterSearch = guion => {
    const matchesSearch = textFilter({
      object: guion,
      fields: ['nombre'],
      search: search
    })
    const matchesAsignatura =
      !selectedAsignatura ||
      guion.asignaturas.some(
        asignatura => asignatura.id === selectedAsignatura.id
      )
    return matchesSearch && matchesAsignatura
  }

  const openModal = guion => {
    setGuionSeleccionado(guion)
    setIsOpenEditarGuion(true)
  }

  const closeModal = () => {
    setIsOpenEditarGuion(false)
    setGuionSeleccionado(null)
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        title='Guiones'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        actions={
          <AsignaturaSelector
            label='Asignatura'
            value={selectedAsignatura?.id || null}
            onChange={(_event, newValue) => setSelectedAsignatura(newValue)}
            className={css.asignaturaFilter}
          />
        }
      />
      <InformationTable
        details={[
          { title: 'Nombre', key: 'nombre', sortKey: 'nombreGuion' },
          {
            title: 'Asignaturas',
            key: 'asignaturas',
            sortKey: 'totalAsignaturas'
          },
          { title: 'Opciones', key: 'opciones', align: 'center' }
        ]}
        data={guionList.filter(filterSearch).map(guion => ({
          nombre: (
            <span
              className={css.link}
              onClick={() => {
                setGuionSeleccionado(guion)
                setIsOpenPdfModal(true)
              }}
            >
              {guion.nombre || '--'}
            </span>
          ),
          nombreGuion: guion.nombre,
          totalAsignaturas: guion.asignaturas.length,
          asignaturas:
            guion.asignaturas.length > 0 ? (
              <span
                className={css.asignaturasCount}
                onClick={() => {
                  setGuionSeleccionado(guion)
                  setIsOpenAsignaturasModal(true)
                }}
              >
                {guion.asignaturas.length}{' '}
                {pluralize('asignatura', guion.asignaturas.length)}
              </span>
            ) : (
              <span className={css.sinAsignaturas}>Sin asignaturas</span>
            ),
          opciones: (
            <>
              <IconButton
                size='small'
                color='secondary'
                disabled={!accesoBrain}
                onClick={() => openModal(guion)}
              >
                <i className='material-icons'>create</i>
              </IconButton>
              <IconButton
                size='small'
                color='error'
                disabled={!accesoBrain}
                onClick={() => {
                  setGuionSeleccionado(guion)
                  setIsOpenEliminarGuion(true)
                }}
              >
                <i className='material-icons'>delete</i>
              </IconButton>
            </>
          )
        }))}
        isLoading={isLoading}
        isFetching={isLoading}
      />
      <ModalNuevoGuion
        open={isModalOpen}
        onSubmit={handleNuevoGuion}
        onClose={() => setIsModalOpen(false)}
        isLoading={isGuionCreating}
      />
      <ModalEditarGuion
        open={isOpenEditarGuion}
        guion={guionSeleccionado}
        onClose={closeModal}
        isLoading={isGuionUpdating}
        onSubmit={handleUpdateGuion}
      />
      <ModalConfirmacion
        title='¿Seguro que quieres borrar el guion?'
        content='Al borrar el guion, dejará de estar disponible en el área de profesores, así como en el área de alumnos de Classfy.'
        open={isOpenEliminarGuion}
        onClose={() => setIsOpenEliminarGuion(false)}
        onClick={handleDeleteGuion}
      />
      <ModalAsignaturas
        open={isOpenAsignaturasModal}
        onClose={() => setIsOpenAsignaturasModal(false)}
        name={guionSeleccionado?.nombre || ''}
        asignaturas={guionSeleccionado?.asignaturas || []}
      />
      <ModalPdfViewer
        open={isOpenPdfModal}
        onClose={() => setIsOpenPdfModal(false)}
        title={guionSeleccionado?.nombre || ''}
        pdfUrl={guionSeleccionado?.url || ''}
      />
      {accesoBrain && (
        <Fab
          className={css.nuevo}
          onClick={() => setIsModalOpen(true)}
          size='medium'
          color='primary'
          aria-label='add'
        >
          <i className='material-icons'>add</i>
        </Fab>
      )}
    </PageLoading>
  )
}

export default Guiones
